/**
 * RTK Query endpoints for platform organization Twilio detail.
 * Covers: Twilio integration, 10DLC apps, number registrations, approved numbers.
 *
 * Phase 1 of Q9b: endpoint definitions only. Hooks not yet consumed by page.
 */

import { baseApi } from '@/api/core/baseApi';
import { unwrapApiResponse, unwrapListResponse } from '@/api/patterns/crud';
import type {
  OrgTwilioData,
  OrgTenDlcApplication,
  OrgNumberRegistration,
  OrgApprovedNumber,
  UpdateTwilioSettingsArgs,
  CreateTenDlcApplicationArgs,
  UpdateTenDlcStatusArgs,
  CreateNumberRegistrationArgs,
  UpdateNumberRegStatusArgs,
  UpdateFocDateArgs,
  LinkApprovedNumberArgs,
  UnlinkApprovedNumberArgs,
  UploadLoaArgs,
  TransferPhoneNumberArgs,
} from '../types';

export const twilioDetailApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // ── Queries ─────────────────────────────────────────────────

    getOrgTwilioIntegration: builder.query<OrgTwilioData, string>({
      query: (orgId) => `/platform/organizations/${orgId}/twilio`,
      transformResponse: unwrapApiResponse<OrgTwilioData>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgTwilio', id: orgId },
      ],
    }),

    getOrgTenDlcApplications: builder.query<OrgTenDlcApplication[], string>({
      query: (orgId) => `/platform/organizations/${orgId}/ten-dlc-applications`,
      transformResponse: unwrapListResponse<OrgTenDlcApplication>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgTenDlc', id: orgId },
      ],
    }),

    getOrgNumberRegistrations: builder.query<OrgNumberRegistration[], string>({
      query: (orgId) => `/platform/organizations/${orgId}/number-registrations`,
      transformResponse: unwrapListResponse<OrgNumberRegistration>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    getOrgApprovedNumbers: builder.query<OrgApprovedNumber[], string>({
      query: (orgId) => `/platform/organizations/${orgId}/approved-numbers`,
      transformResponse: unwrapListResponse<OrgApprovedNumber>,
      providesTags: (_result, _error, orgId) => [
        { type: 'PlatformOrgApprovedNumbers', id: orgId },
      ],
    }),

    // ── Twilio Settings Mutation ────────────────────────────────

    updateOrgTwilioSettings: builder.mutation<OrgTwilioData, UpdateTwilioSettingsArgs>({
      query: ({ orgId, settings }) => ({
        url: `/platform/organizations/${orgId}/twilio`,
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgTwilio', id: orgId },
      ],
    }),

    // ── 10DLC Mutations ─────────────────────────────────────────

    createTenDlcApplication: builder.mutation<OrgTenDlcApplication, CreateTenDlcApplicationArgs>({
      query: ({ orgId, form }) => ({
        url: `/platform/organizations/${orgId}/ten-dlc-applications`,
        method: 'POST',
        body: form,
      }),
      transformResponse: unwrapApiResponse<OrgTenDlcApplication>,
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgTenDlc', id: orgId },
      ],
    }),

    updateTenDlcStatus: builder.mutation<OrgTenDlcApplication, UpdateTenDlcStatusArgs>({
      query: ({ orgId, applicationId, status, campaignSid }) => {
        const body: Record<string, string> = { status };
        if (campaignSid !== undefined) {
          body.campaignSid = campaignSid;
        }
        return {
          url: `/platform/organizations/${orgId}/ten-dlc-applications/${applicationId}/status`,
          method: 'PUT',
          body,
        };
      },
      transformResponse: unwrapApiResponse<OrgTenDlcApplication>,
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgTenDlc', id: orgId },
      ],
    }),

    // ── Number Registration Mutations ───────────────────────────

    createNumberRegistration: builder.mutation<OrgNumberRegistration, CreateNumberRegistrationArgs>({
      query: ({ orgId, form }) => ({
        url: `/platform/organizations/${orgId}/number-registrations`,
        method: 'POST',
        body: form,
      }),
      transformResponse: unwrapApiResponse<OrgNumberRegistration>,
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    updateNumberRegStatus: builder.mutation<unknown, UpdateNumberRegStatusArgs>({
      query: ({ orgId, registrationId, status }) => ({
        url: `/platform/organizations/${orgId}/number-registrations/${registrationId}/status`,
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    updateFocDate: builder.mutation<unknown, UpdateFocDateArgs>({
      query: ({ orgId, registrationId, focDate }) => ({
        url: `/platform/organizations/${orgId}/number-registrations/${registrationId}/foc-date`,
        method: 'PUT',
        body: { focDate },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    // ── Approved Numbers Mutations ──────────────────────────────

    linkApprovedNumber: builder.mutation<OrgApprovedNumber, LinkApprovedNumberArgs>({
      query: ({ orgId, tenDlcApplicationId, numberRegistrationId, brandSid, campaignSid }) => ({
        url: `/platform/organizations/${orgId}/approved-numbers`,
        method: 'POST',
        body: {
          tenDlcApplicationId,
          numberRegistrationId,
          brandSid: brandSid || null,
          campaignSid: campaignSid || null,
        },
      }),
      transformResponse: unwrapApiResponse<OrgApprovedNumber>,
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgApprovedNumbers', id: orgId },
        { type: 'PlatformOrgTenDlc', id: orgId },
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    unlinkApprovedNumber: builder.mutation<unknown, UnlinkApprovedNumberArgs>({
      query: ({ orgId, approvedNumberId }) => ({
        url: `/platform/organizations/${orgId}/approved-numbers/${approvedNumberId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgApprovedNumbers', id: orgId },
        { type: 'PlatformOrgTenDlc', id: orgId },
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    // ── File Operations ─────────────────────────────────────────

    uploadLoa: builder.mutation<unknown, UploadLoaArgs>({
      query: ({ orgId, registrationId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/platform/organizations/${orgId}/number-registrations/${registrationId}/upload/loa`,
          method: 'POST',
          body: formData,
          // Let the browser set Content-Type with boundary for FormData
          formData: true,
        };
      },
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),

    // Note: downloadFile returns a blob (not JSON) — remains as raw fetch
    // in the action hooks (imperative blob download doesn't fit RTK).

    transferPhoneNumber: builder.mutation<unknown, TransferPhoneNumberArgs>({
      query: ({ orgId, registrationId, targetAccountSid }) => ({
        url: `/platform/organizations/${orgId}/number-registrations/${registrationId}/transfer`,
        method: 'POST',
        body: { targetAccountSid },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'PlatformOrgNumberReg', id: orgId },
      ],
    }),
  }),
});

export const {
  // Queries
  useGetOrgTwilioIntegrationQuery,
  useGetOrgTenDlcApplicationsQuery,
  useGetOrgNumberRegistrationsQuery,
  useGetOrgApprovedNumbersQuery,
  // Twilio settings
  useUpdateOrgTwilioSettingsMutation,
  // 10DLC
  useCreateTenDlcApplicationMutation,
  useUpdateTenDlcStatusMutation,
  // Number registrations
  useCreateNumberRegistrationMutation,
  useUpdateNumberRegStatusMutation,
  useUpdateFocDateMutation,
  // Approved numbers
  useLinkApprovedNumberMutation,
  useUnlinkApprovedNumberMutation,
  // File operations
  useUploadLoaMutation,
  // Phone number transfer
  useTransferPhoneNumberMutation,
} = twilioDetailApi;
